import { useHirerAuth } from '@seek/hirer-auth-react';
import type { ChangeEvent } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import loadable from 'sku/@loadable/component';

import { SystemBanner } from 'src/app/shared/components/SystemBanner';
import { UivController } from 'src/app/shared/components/UivController';
import { TRACKING_ORIGIN } from 'src/app/shared/constants/headerFooterTracking';
import { PAGE_ID_SUCCESS } from 'src/app/shared/constants/pageUid';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import { isProd } from 'src/app/utils/environment';
import { log } from 'src/app/utils/logging';
import { useAnalyticsTracking } from 'src/app/v2/hooks/useAnalyticsTracking';
import { useHeaderFooterContext } from 'src/app/v2/hooks/useHeaderFooterContext';
import { useHirerAccount } from 'src/app/v2/hooks/useHirerAccount';
import { usePage } from 'src/app/v2/hooks/usePage';
import { useRedirectToLastValidRoute } from 'src/app/v2/hooks/useRedirectToLastValidRoute';
import { useScrollToTop } from 'src/app/v2/hooks/useScrollToTop';
import { BeforeHeader, MainLayout, AfterHeader } from 'src/app/v2/layouts/main';
import type { ContainerProps } from 'src/types/app';

const TrackingDrawer = loadable(
  () =>
    import('src/app/shared/dataAnalytics/components/TrackingDrawer').then(
      (module) => ({
        default: module.TrackingDrawer,
      }),
    ),
  { ssr: false },
);

export const CreateFlowContainerV2 = (props: ContainerProps) => {
  const headerFooterContext = useHeaderFooterContext();
  const hirerAccount = useHirerAccount();
  const { getSite, getLocale } = useConfig();
  const pageId = usePage();
  const { trackingAdapter } = useAnalyticsTracking();
  const { loading, authenticated } = useHirerAuth();
  useRedirectToLastValidRoute();
  useScrollToTop();

  const onToggleButtonClick = (event: ChangeEvent) => {
    const trackingEvent = event.target.getAttribute('data-analytics');

    if (trackingEvent) {
      const eventData = {
        eventName: trackingEvent,
        objectInteraction: TRACKING_ORIGIN.HEADER,
      };
      trackingAdapter.sendLinkEvent(eventData);
    }
  };

  if (loading) {
    return null;
  }

  return (
    <ErrorBoundary
      /* TODO: Render a general error page*/
      FallbackComponent={() => <></>}
      onError={(error) => {
        log.error(
          'CreateFlowContainerV2 | ErrorBoundary | error',
          {
            error,
            seekAdvertiserId: hirerAccount?.id,
          },
          true,
        );
      }}
    >
      <MainLayout
        headerFooterContext={
          authenticated ? headerFooterContext : { identityContext: null }
        }
        site={getSite()}
        locale={getLocale()}
        basicHeader={pageId !== PAGE_ID_SUCCESS}
        onToggleButtonClick={onToggleButtonClick}
      >
        <BeforeHeader>
          <UivController />
        </BeforeHeader>
        <AfterHeader>
          <SystemBanner context="job_posting_global" />
        </AfterHeader>
        {props.children}
        {!isProd() && <TrackingDrawer />}
      </MainLayout>
    </ErrorBoundary>
  );
};
